import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'

import { TerminologyModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import { copyText } from './copyResourceLink'
import { termCitations } from './termCitation'

const TEXT_FORMATS = [
    { key: 'apa', label: 'APA' },
    { key: 'mla', label: 'MLA' },
]

const FILE_FORMATS = [
    { key: 'bibtex', label: 'BibTeX', hint: 'for LaTeX', ext: 'bib', mime: 'application/x-bibtex' },
    { key: 'ris', label: 'RIS', hint: 'for Zotero, EndNote, Mendeley', ext: 'ris', mime: 'application/x-research-info-systems' },
]

const panelSx = { bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }

const CiteTermModal = ({ id, term, updated, buttonProps, sx }) => {
    const dispatch = useDispatch()
    const [open, setOpen] = useState(false)
    const citations = open ? termCitations({ id, term, updated }) : {}

    const handleCopy = (text) => copyText({
        dispatch,
        text,
        successMessage: 'Citation copied to clipboard',
        errorMessage: 'Unable to copy citation. Please copy the text manually.',
    })

    return (
        <>
            <Button {...buttonProps} sx={sx} onClick={() => setOpen(true)}>
                Cite&nbsp;<FormatQuoteIcon />
            </Button>
            <BootstrapDialog
                onClose={() => setOpen(false)}
                aria-labelledby={`cite-term-modal-${id}`}
                open={open}
                fullWidth
                {...TerminologyModalStyle.modal}
            >
                <BootstrapDialogTitle id={`cite-term-modal-${id}`} onClose={() => setOpen(false)}>
                    <Box sx={TerminologyModalStyle.headingContainer}>
                        <FormatQuoteIcon sx={TerminologyModalStyle.icon} />
                        <Box>
                            Cite this term
                            <Typography variant="body2" color="text.secondary">{term}</Typography>
                        </Box>
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Typography {...TerminologyModalStyle.text} component="p" sx={{ mb: 2 }}>
                        Copy a citation, or download it for your reference manager.
                    </Typography>
                    {TEXT_FORMATS.map(({ key, label }) => (
                        <Box key={key} sx={TerminologyModalStyle.additionalInfo}>
                            <Typography {...TerminologyModalStyle.heading} component="p" gutterBottom>{label}</Typography>
                            <Box sx={{ ...panelSx, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                                    {citations[key]}
                                </Typography>
                                <Tooltip title="Copy">
                                    <IconButton size="small" aria-label={`Copy ${label} citation`} onClick={() => handleCopy(citations[key])}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))}
                    {FILE_FORMATS.map(({ key, label, hint, ext, mime }) => (
                        <Accordion key={key} disableGutters variant="outlined" sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography {...TerminologyModalStyle.heading}>{label}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                                    {hint}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography
                                    component="pre"
                                    variant="body2"
                                    sx={{ ...panelSx, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', overflowX: 'auto', m: 0 }}
                                >
                                    {citations[key]}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Button size="small" aria-label={`Copy ${label} citation`} startIcon={<ContentCopyIcon />} onClick={() => handleCopy(citations[key])}>
                                        Copy
                                    </Button>
                                    <Button
                                        size="small"
                                        component="a"
                                        aria-label={`Download ${label} citation`}
                                        startIcon={<DownloadIcon />}
                                        href={`data:${mime};charset=utf-8,${encodeURIComponent(citations[key])}`}
                                        download={`tcairem-glossary-${id}.${ext}`}
                                    >
                                        Download
                                    </Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </BootstrapDialog>
        </>
    )
}

CiteTermModal.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    term: PropTypes.string.isRequired,
    updated: PropTypes.string.isRequired,
    buttonProps: PropTypes.object,
    sx: PropTypes.object,
}

export default CiteTermModal
